'use client';

import Image from 'next/image';
import { useRouter } from 'next/navigation';

import { CertificateIcon } from '@/components/Icons/Icons';
import { CARD_FALLBACK_IMAGE } from '@/lib/utils/thumbnail-url';
import LearningPathEnrollButton from '@/components/common/learning-path-enroll-button';
import { LearningPathStats } from '@/components/common/learning-path-stats';

interface LearningPathCardProps {
  url: string;
  uid?: string;
  thumbnail?: string;
  title: string;
  description: string;
  hasCertificate?: boolean;
  coursesCount?: number;
  videosCount?: number;
  quizzesCount?: number;
  assessmentCount?: number;
  duration?: string;
  showProgress?: boolean;
  progressPercent?: number;
  isCompleted?: boolean;
  isLearningMaterialCompleted?: boolean;
  hasAssessment?: boolean;
  isEnrolled?: boolean;
  hasStarted?: boolean;
  onClick?: () => void;
}

export default function LearningPathCard({
  url,
  uid,
  thumbnail,
  title,
  description,
  hasCertificate = false,
  coursesCount,
  videosCount,
  quizzesCount,
  assessmentCount,
  duration,
  showProgress = false,
  progressPercent = 0,
  isCompleted,
  isLearningMaterialCompleted = false,
  hasAssessment = false,
  isEnrolled = false,
  hasStarted = false,
  onClick,
}: LearningPathCardProps) {
  const router = useRouter();

  const baseUrl = url.startsWith('/') ? url : `/${url}`;
  const navigateUrl = hasAssessment && isLearningMaterialCompleted && !isCompleted
    ? `${baseUrl}/assessment`
    : baseUrl;

  const handleCardClick = () => {
    router.push(baseUrl);
    onClick?.();
  };

  return (
    <div className="bg-surface-2 mx-auto flex flex-col lg:flex-row cursor-pointer card-hover-zoom group transition-all" onClick={handleCardClick}>
        <div className="relative h-[238px] w-full lg:w-[30%] overflow-hidden">
          <Image
            src={
              thumbnail && thumbnail.trim() !== ''
                ? `${thumbnail}?width=800`
                : CARD_FALLBACK_IMAGE
            }
            alt={title}
            fill
            className="object-cover card-zoom-image"
            sizes="(max-width: 1024px) 100vw, 30vw"
          />
        </div>

        <div className="achievement-card-bg w-full p-4 sm:p-6 lg:p-8 lg:w-[70%]">
          <div className="flex flex-col items-start">
            <span className="cs-caption">Learning Path</span>
            <h3 className="type-h3 mb-2 line-clamp-2">{title}</h3>
            <p className="cs-m-body line-clamp-2">{description}</p>
          </div>

          <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 lg:gap-8">
            {/* Metadata - First on mobile, second on desktop */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 order-1 sm:order-2">
              {/* Certificate - First line on mobile, inline on desktop */}
              {hasCertificate && (
                <>
                  <div className="flex items-center gap-2">
                    <CertificateIcon width={20} height={20} fill="var(--Text-Default-Strong)" fillOpacity="0.6" />
                    <span className="cs-caption">Certificate</span>
                  </div>
                  {/* {(coursesCount !== undefined || videosCount !== undefined || quizzesCount !== undefined || assessmentCount !== undefined) && (
                    <div className="hidden sm:block"><span className="divider" /></div>
                  )} */}
                </>
              )}

              {/* Stats: Mobile/Tab = icons + text (like home card), Desktop = text with dividers */}
              {(coursesCount !== undefined || videosCount !== undefined || quizzesCount !== undefined || assessmentCount !== undefined || duration) && (
                <LearningPathStats
                  stats={{
                    courses: coursesCount ?? 0,
                    videos: videosCount ?? 0,
                    quizzes: quizzesCount ?? 0,
                    assessments: assessmentCount ?? 0,
                  }}
                  duration={duration}
                  variant="home"
                />
              )}
            </div>

            {/* Button - Second on mobile (bottom), first on desktop; same hover as profile card */}
            <div onClick={(e) => e.stopPropagation()} className="order-2 sm:order-1 w-full sm:w-auto">
              <LearningPathEnrollButton
                learningPathUid={uid}
                initialIsSubscribed={isEnrolled}
                initialHasStarted={hasStarted}
                progressPercent={progressPercent}
                isCompleted={isCompleted}
                isLearningMaterialCompleted={isLearningMaterialCompleted}
                hasAssessment={hasAssessment}
                navigateUrl={navigateUrl}
                variant="SecondaryFillButton"
                className="w-full sm:w-auto btn-primary-fill-group-hover [&_svg]:transition-transform [&_svg]:duration-300 hover:[&_svg]:translate-x-1"
              />
            </div>
          </div>
        </div>
      </div>
  );
}
